import { Alert, Anchor, Box, Card, Divider, Grid, List, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconExternalLink } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import Metadata from '../../components/Metadata';
import { Page } from '../../constants/pageTitles';
import { themeEditorAppEmbedUrl } from '../../helpers/shopifyAppEmbed';
import useAppEmbedStatus from '../../hooks/useAppEmbedStatus';

const ShopifyIntegrationPage = () => {
  const page = `${Page['shopifyIntegration']}`;

  const domain = useSelector((state) => state.domain.domain);

  // This page is where a merchant lands to fix an embed, so it checks rather
  // than reporting whatever was last written. Without that it would show the
  // red banner, hand them the link that fixes it, and still show the red banner
  // when they came back — see useAppEmbedStatus. The live answer wins; the
  // stored value covers the moment before the first check returns.
  const { appEmbedActive: live } = useAppEmbedStatus({ domain, enabled: domain?.platform === 'shopify' });
  const appEmbedActive = live ?? domain.appEmbedActive;

  return (
    <div>
      <Metadata title={page} />
      <Grid pt={30} my={20} ta="left">
        <Grid.Col span={12}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Box pb="md">
                <Title order={2} fw={700}>
                  {page}
                </Title>
                <Title order={5}>Activate Releva in your Shopify shop in 1 easy step.</Title>
              </Box>
              <Divider my="sm" />

              {appEmbedActive === true && (
                <Alert color="green" icon={<IconCheck size={16} />} mb="md">
                  The Releva.ai app embed is enabled. Your shop is being tracked.
                </Alert>
              )}
              {appEmbedActive === false && (
                <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="md">
                  The Releva.ai app embed is not enabled, so nothing is being tracked and no personalization is being
                  shown.
                </Alert>
              )}
              {appEmbedActive == null && (
                <Alert color="gray" icon={<IconAlertTriangle size={16} />} mb="md">
                  We cannot confirm whether the Releva.ai app embed is enabled.
                </Alert>
              )}

              {/* Three steps rather than one, because the Save is the step
                  merchants miss: toggling the embed on looks like the whole job,
                  but Shopify discards it unless the theme is saved, so the shop
                  stays untracked and the toggle appears off again next time. */}
              <List type="ordered" pb="lg" spacing="sm">
                <List.Item>
                  Open{' '}
                  <Anchor href={themeEditorAppEmbedUrl(domain)} target="_blank" rel="noreferrer">
                    your Shopify theme editor <IconExternalLink size={14} />
                  </Anchor>{' '}
                  (opens in a new tab).
                </List.Item>
                <List.Item>
                  Switch the <b>Releva.ai</b> app embed on.
                </List.Item>
                <List.Item>
                  Click <b>Save</b> in the top-right corner. <b>The embed is not active until you save</b> — Shopify
                  discards the change otherwise.
                </List.Item>
              </List>

              <Divider my="sm" />
              <Text c="dimmed">
                Releva Uses a single app embed (Releva.ai) to collect user behavior and render web personalization
                elements according to their configuration in Releva. <br />
                If you wish to stop the Releva integration, it is sufficient to disable the Releva.ai app embed.
              </Text>
            </Card.Section>
          </Card>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default ShopifyIntegrationPage;
